import { isOutletUser } from './adminAccessControl';
import {
  cacheScopedOutlets,
  clearAdminSession,
  readCachedScopedOutlets,
  readStoredSessionToken,
} from '../../utils/adminSession';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
export const POS_API_BASE_URL = process.env.REACT_APP_API_PRODUCT_URL || '';

export const POS_PRODUCT_ITEM_TYPE_OPTIONS = [
  { value: 'resale_item', label: 'POS Jual Langsung' },
  { value: 'raw_material', label: 'Bahan baku' },
  { value: 'finished_good', label: 'POS Dengan Bahan Baku' },
  { value: 'service', label: 'Laundry / Jasa' },
];

export const isSuccessResponse = value => value === 0 || value === '0';

export const parseJsonResponse = async response => {
  const raw = await response.text();
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return {
      error: 1,
      message: raw,
    };
  }
};

export const buildApiErrorMessage = (response, data, fallback) => {
  if (response?.status === 404) {
    return 'Endpoint POS belum aktif di backend. Restart service POS lalu buka ulang halaman ini.';
  }

  if (typeof data?.error === 'string' && data.error.trim()) {
    return data.error;
  }

  return data?.message || fallback;
};

export const buildFetchErrorMessage = (error, fallback, options = {}) => {
  const rawMessage = String(error?.message || '').trim();
  const normalizedMessage = rawMessage.toLowerCase();
  const backendUrl = String(options?.backendUrl || POS_API_BASE_URL).trim();

  if (
    normalizedMessage === 'failed to fetch' ||
    normalizedMessage.includes('networkerror') ||
    normalizedMessage.includes('load failed')
  ) {
    if (backendUrl) {
      return `Tidak bisa terhubung ke backend POS di ${backendUrl}. Jalankan service possystem lalu coba scan lagi.`;
    }

    return 'Tidak bisa terhubung ke backend POS. Jalankan service possystem lalu coba scan lagi.';
  }

  return rawMessage || fallback;
};

export const buildAuthHeaders = (token, extraHeaders = {}) => ({
  ...extraHeaders,
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export const normalizeOutletRecord = outlet => ({
  id: String(outlet?.id ?? outlet?.ID ?? outlet?.outlet_id ?? ''),
  name:
    outlet?.outlet_name ||
    outlet?.OutletName ||
    outlet?.name ||
    `Outlet #${outlet?.id || outlet?.ID || outlet?.outlet_id || '-'}`,
});

export const resolveCurrentUserOutlet = currentUser => {
  const id = String(
    currentUser?.outlet_id ??
    currentUser?.OutletID ??
    currentUser?.outlet ??
    ''
  ).trim();

  if (!id) {
    return null;
  }

  return {
    id,
    name:
      currentUser?.outlet_name ||
      currentUser?.OutletName ||
      currentUser?.outletName ||
      `Outlet #${id}`,
  };
};

export const getInitialScopedOutlets = currentUser => {
  const cachedOutlets = readCachedScopedOutlets(currentUser);
  if (cachedOutlets.length > 0) {
    return cachedOutlets;
  }

  const ownOutlet = resolveCurrentUserOutlet(currentUser);
  if (isOutletUser(currentUser) && ownOutlet?.id) {
    return [ownOutlet];
  }

  return [];
};

export const resolveAdminSessionToken = async (verifyAndRefreshToken, navigate) => {
  const token = readStoredSessionToken();
  if (!token) {
    navigate('/loginadmin');
    return '';
  }

  const isValid = await verifyAndRefreshToken(token);
  if (!isValid) {
    clearAdminSession();
    navigate('/loginadmin');
    return '';
  }

  return readStoredSessionToken() || '';
};

export const fetchScopedOutlets = async ({ token, currentUser }) => {
  const ownOutlet = resolveCurrentUserOutlet(currentUser);
  if (isOutletUser(currentUser) && ownOutlet?.id) {
    cacheScopedOutlets(currentUser, [ownOutlet]);
    return [ownOutlet];
  }

  const cachedOutlets = readCachedScopedOutlets(currentUser);
  if (cachedOutlets.length > 0) {
    return cachedOutlets;
  }

  const response = await fetch(`${API_BASE_URL}outletlist`, {
    method: 'POST',
    headers: buildAuthHeaders(token, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({
      limit: 500,
      start: 0,
    }),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat outlet.'));
  }

  const records = (Array.isArray(data?.results) ? data.results : [])
    .map(normalizeOutletRecord)
    .filter(outlet => outlet.id);

  if (records.length === 0 && ownOutlet?.id) {
    cacheScopedOutlets(currentUser, [ownOutlet]);
    return [ownOutlet];
  }

  cacheScopedOutlets(currentUser, records);
  return records;
};

export const normalizePagedRecords = data => {
  const records = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.results)
        ? data.results
        : [];

  return {
    records,
    total:
      Number(
        data?.totalItems ??
        data?.total ??
        data?.count ??
        records.length
      ) || records.length,
  };
};

export const formatCurrency = value =>
  `Rp ${Number(value || 0).toLocaleString('id-ID', {
    maximumFractionDigits: 0,
  })}`;

export const formatShortDateTime = value => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateForInput = value => {
  if (!value) {
    return '';
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const humanizeToken = value =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ') || '-';

export const getInitials = value => {
  const tokens = String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (tokens.length === 0) {
    return 'NA';
  }

  return tokens
    .slice(0, 2)
    .map(token => token.charAt(0).toUpperCase())
    .join('');
};

export const getItemTypeLabel = value =>
  POS_PRODUCT_ITEM_TYPE_OPTIONS.find(option => option.value === value)?.label || 'POS Jual Langsung';

export const getItemTypeBadgeClass = value => {
  switch (String(value || '').toLowerCase()) {
    case 'raw_material':
      return 'pos-catalog-badge pos-catalog-badge--warning';
    case 'finished_good':
      return 'pos-catalog-badge pos-catalog-badge--info';
    case 'service':
      return 'pos-catalog-badge pos-catalog-badge--muted';
    default:
      return 'pos-catalog-badge pos-catalog-badge--success';
  }
};

export const isStockTrackedProduct = product => {
  const itemType = String(product?.item_type || 'resale_item').toLowerCase();
  return itemType !== 'service';
};

export const getBooleanStatusClass = value =>
  value ? 'status-pill' : 'status-pill is-muted';

export const getSyncStatusLabel = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'synced':
      return 'Synced';
    case 'pending':
      return 'Pending';
    case 'failed':
      return 'Failed';
    default:
      return 'Draft';
  }
};

export const getSyncStatusBadgeClass = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'synced':
      return 'pos-catalog-badge pos-catalog-badge--success';
    case 'pending':
      return 'pos-catalog-badge pos-catalog-badge--warning';
    case 'failed':
      return 'pos-catalog-badge pos-catalog-badge--danger';
    default:
      return 'pos-catalog-badge pos-catalog-badge--muted';
  }
};

export const buildPosImageUrl = filename =>
  filename ? `${POS_API_BASE_URL}datauploads/${filename}` : '';
